// Hermes ECharts theme.
//
// Mirrors the structure of @apexl/icarus-dynamic-components'
// `services/echartsTheme.js` (buildTcEchartsTheme / registerTcEchartsTheme)
// but produces a parallel **'hermes'** theme from the Hermes `--viz-*`
// palette + Inter + the ink/axis chrome tokens. The package's 'tc' theme is
// never touched — we register under a distinct name so `echarts.init(el,'hermes')`
// (and `useChart(el, opts, { theme: 'hermes' })`) pick this up.
//
// Colours are read from CSS custom properties at registration time
// (getComputedStyle on :root) so the theme tracks light/dark token values,
// with a literal hex fallback for the (SSR / not-yet-painted) case where the
// var resolves empty. Charts still re-read via re-registration if the palette
// must follow a live theme flip; for Phase 0 we register once at boot.
//
// Usage (index.js at boot):
//   import * as echarts from 'echarts';
//   import { registerHermesEchartsTheme, HERMES_THEME_NAME } from '@root/services/hermesEchartsTheme';
//   registerHermesEchartsTheme(echarts);
//   const chart = echarts.init(el, HERMES_THEME_NAME);

export const HERMES_THEME_NAME = 'hermes';

// Literal fallbacks — the light-theme values from _tokens.scss / DESIGN.md.
// Used only when a CSS var can't be resolved (no document, or var unset).
const FALLBACK = {
  '--viz-1': '#0EA5E9',
  '--viz-2': '#6366F1',
  '--viz-3': '#0D9488',
  '--viz-4': '#F59E0B',
  '--viz-5': '#E11D48',
  '--viz-neutral': '#94A3B8',
  '--viz-axis': '#94A3B8',
  '--viz-grid': '#E2E8F0',
  '--viz-ink': '#0F172A',
  '--viz-ink-2': '#64748B',
  '--viz-muted': '#94A3B8',
  '--h-bg-overlay': 'rgba(15,23,42,0.55)',
};

const FONT_FAMILY =
  '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const FONT_MONO =
  '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace';

/**
 * Read a CSS custom property off :root, trimming, with a literal fallback.
 * @param {string} name — e.g. '--viz-1'
 * @param {string} [fallback]
 */
function cssVar(name, fallback) {
  const fb = fallback ?? FALLBACK[name] ?? '#000000';
  if (typeof window === 'undefined' || typeof document === 'undefined') return fb;
  try {
    const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return val || fb;
  } catch (e) {
    return fb;
  }
}

/**
 * Resolve the Hermes viz palette + chrome tokens from CSS vars (or fallbacks).
 * Exported so callers can introspect what the theme was built from.
 */
export function resolveHermesPalette() {
  return {
    palette: [
      cssVar('--viz-1'),
      cssVar('--viz-2'),
      cssVar('--viz-3'),
      cssVar('--viz-4'),
      cssVar('--viz-5'),
      cssVar('--viz-neutral'),
    ],
    axis: cssVar('--viz-axis'),
    grid: cssVar('--viz-grid'),
    ink: cssVar('--viz-ink'),
    ink2: cssVar('--viz-ink-2'),
    muted: cssVar('--viz-muted'),
    tooltipBg: 'rgba(15, 23, 42, 0.95)',
  };
}

/**
 * Build the ECharts theme object. Exported separately from registration so
 * tests / tooling can introspect the structure without echarts involved.
 * @param {object} [tokens] — override of `resolveHermesPalette()` output.
 */
export function buildHermesEchartsTheme(tokens) {
  const t = tokens || resolveHermesPalette();
  return {
    color: t.palette,
    backgroundColor: 'transparent',

    textStyle: {
      fontFamily: FONT_FAMILY,
      color: t.ink2,
    },

    title: {
      textStyle: { color: t.ink, fontFamily: FONT_FAMILY, fontSize: 14, fontWeight: 600 },
      subtextStyle: { color: t.muted, fontSize: 12 },
    },

    legend: {
      textStyle: { color: t.ink2, fontSize: 12 },
      itemWidth: 12,
      itemHeight: 8,
      icon: 'roundRect',
    },

    tooltip: {
      backgroundColor: t.tooltipBg,
      borderColor: 'transparent',
      textStyle: { color: '#FFFFFF', fontSize: 12, fontFamily: FONT_FAMILY },
      padding: [8, 12],
      axisPointer: { lineStyle: { color: t.axis }, crossStyle: { color: t.axis } },
    },

    grid: { containLabel: true, left: '3%', right: '4%', top: 32, bottom: 24 },

    categoryAxis: {
      axisLine: { lineStyle: { color: t.grid } },
      axisTick: { show: false },
      axisLabel: { color: t.muted, fontSize: 11 },
      splitLine: { show: false },
    },
    valueAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: t.muted, fontSize: 11, fontFamily: FONT_MONO },
      splitLine: { lineStyle: { color: t.grid, type: 'solid' } },
    },
    timeAxis: {
      axisLine: { lineStyle: { color: t.grid } },
      axisTick: { show: false },
      axisLabel: { color: t.muted, fontSize: 11 },
      splitLine: { show: false },
    },

    line: {
      smooth: true,
      symbol: 'circle',
      symbolSize: 5,
      lineStyle: { width: 2 },
    },
    bar: {
      itemStyle: { borderRadius: [3, 3, 0, 0] },
    },
    pie: {
      itemStyle: { borderColor: cssVar('--viz-surface', '#FFFFFF'), borderWidth: 2 },
      label: { color: t.ink2, fontSize: 11 },
    },
  };
}

/**
 * Register the Hermes theme onto the supplied echarts module. Idempotent
 * (last registration wins). NEVER mutates the package's 'tc' theme — it
 * registers under `HERMES_THEME_NAME` only.
 *
 * @param {object} echarts — the echarts module (caller imports + passes in)
 * @param {object} [tokens] — palette override (see resolveHermesPalette)
 * @param {string} [name=HERMES_THEME_NAME]
 */
export function registerHermesEchartsTheme(echarts, tokens, name = HERMES_THEME_NAME) {
  if (!echarts || typeof echarts.registerTheme !== 'function') return;
  echarts.registerTheme(name, buildHermesEchartsTheme(tokens));
}

export default registerHermesEchartsTheme;
